/**
 * Gestão de usuários.
 *
 * Os dados aparecem em claro, e não mascarados. O mascaramento do manual (§7)
 * protege **titulares** — as pessoas que o bHunter perfila e que nunca se
 * cadastraram. Os operadores do sistema são outra coisa: um administrador precisa
 * ver o e-mail e o telefone para onde o segundo fator vai, e escondê-los seria
 * teatro. A distinção é deliberada.
 */
import { Archive, ArchiveRestore, KeyRound, Pencil, Plus, Trash2, UserCheck, UserX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CampoTelefone } from '@/components/CampoTelefone';
import { ConfirmarAcao } from '@/components/ConfirmarAcao';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Sheet } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Chip, Skeleton, Spinner } from '@/components/ui/misc';
import { api, type Usuario } from '@/lib/api';
import { useSessao } from '@/lib/auth';
import { formatarTelefone } from '@/lib/telefone';

type Formulario = { nome: string; email: string; telefone: string };
const VAZIO: Formulario = { nome: '', email: '', telefone: '' };

function Campos({
  form,
  setForm,
  prefixo,
}: {
  form: Formulario;
  setForm: (f: Formulario) => void;
  prefixo: string;
}) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${prefixo}-nome`}>Nome</Label>
        <Input
          id={`${prefixo}-nome`}
          required
          minLength={2}
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${prefixo}-email`}>E-mail</Label>
        {/* Identificador: mono (manual §2). */}
        <Input
          id={`${prefixo}-email`}
          type="email"
          required
          className="font-mono"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </div>
      <CampoTelefone
        id={`${prefixo}-telefone`}
        rotulo="Telefone"
        valor={form.telefone}
        onChange={(e164) => setForm({ ...form, telefone: e164 })}
      />
    </>
  );
}

export function Usuarios() {
  const { usuario: eu, recarregar } = useSessao();
  const [lista, setLista] = useState<Usuario[] | null>(null);
  const [criando, setCriando] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [novo, setNovo] = useState<Formulario>(VAZIO);
  const [edicao, setEdicao] = useState<Formulario>(VAZIO);
  const [aExcluir, setAExcluir] = useState<Usuario | null>(null);
  // Arquivados somem da lista por padrão. É o ponto de arquivar: tirar do caminho
  // quem não trabalha mais aqui, sem apagar o histórico dele.
  const [mostrarArquivados, setMostrarArquivados] = useState(false);

  async function carregar() {
    const r = await api.get<Usuario[]>('/usuarios');
    if (r.ok) setLista(r.dados);
    else toast.error(r.erro);
  }

  useEffect(() => {
    void carregar();
  }, []);

  async function criar(e: React.FormEvent) {
    e.preventDefault();
    setOcupado('criar');
    const r = await api.post<Usuario>('/usuarios', novo);
    setOcupado(null);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(`${r.dados.nome} cadastrado. A senha temporária foi enviada por e-mail.`);
    setNovo(VAZIO);
    setCriando(false);
    void carregar();
  }

  async function salvar(e: React.FormEvent, u: Usuario) {
    e.preventDefault();
    setOcupado(u.id);
    const r = await api.put<Usuario>(`/usuarios/${u.id}`, edicao);
    setOcupado(null);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(`${r.dados.nome} atualizado.`);
    setEditandoId(null);
    void carregar();
    if (u.id === eu?.id) void recarregar(); // o próprio nome no topo muda
  }

  async function resetarSenha(u: Usuario) {
    setOcupado(u.id);
    const r = await api.post(`/usuarios/${u.id}/resetar-senha`);
    setOcupado(null);
    if (!r.ok) return void toast.error(r.erro);
    // Voz factual, nomeando o que aconteceu (manual §9).
    toast.success(`Senha de ${u.nome} reemitida e enviada para ${u.email}.`);
    void carregar();
  }

  async function excluir(u: Usuario) {
    setOcupado(u.id);
    const r = await api.del(`/usuarios/${u.id}`);
    setOcupado(null);
    setAExcluir(null);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(`${u.nome} excluído. O registro do que ele fez permanece na auditoria.`);
    void carregar();
  }

  const PASSADO: Record<string, string> = {
    desativar: 'desativado',
    arquivar: 'arquivado',
    reativar: 'reativado',
  };

  async function mudarEstado(u: Usuario, acao: 'desativar' | 'arquivar' | 'reativar') {
    setOcupado(u.id);
    const r = await api.post<Usuario>(`/usuarios/${u.id}/${acao}`);
    setOcupado(null);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(`${u.nome} ${PASSADO[acao]}.`);
    void carregar();
  }

  const arquivados = lista?.filter((u) => u.estado === 'arquivado').length ?? 0;
  const visiveis = (lista ?? []).filter((u) => mostrarArquivados || u.estado !== 'arquivado');

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="tipo-titulo">Usuários</h1>
          <p className="mt-2 text-[13px] leading-normal text-text-secondary">
            Todos são administradores por enquanto. Os papéis chegam na Fase 4.
          </p>
        </div>
        {/* Único âmbar da tela. Abre o drawer lateral — o mesmo padrão de criação das
            entidades de cadastro. A edição segue inline na tabela (ver abaixo). */}
        <Button variant="primario" onClick={() => setCriando(true)}>
          <Plus />
          Cadastrar usuário
        </Button>
      </div>

      <Sheet
        aberto={criando}
        onAbrir={setCriando}
        titulo="Novo usuário"
        descricao="O sistema gera uma senha temporária e a envia por e-mail. Ela precisa ser trocada no primeiro acesso. O telefone recebe o código de verificação por WhatsApp."
        rodape={
          <>
            <Button type="button" variant="secundario" onClick={() => setCriando(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="form-novo-usuario" variant="primario" disabled={ocupado === 'criar'}>
              {ocupado === 'criar' ? <Spinner /> : 'Cadastrar'}
            </Button>
          </>
        }
      >
        <form id="form-novo-usuario" onSubmit={criar} className="flex flex-col gap-4">
          <Campos form={novo} setForm={setNovo} prefixo="novo" />
        </form>
      </Sheet>

      <Card>
        <CardContent className="p-0">
          {lista === null ? (
            <div className="flex flex-col gap-3 p-6">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="tipo-rotulo p-4">Nome</th>
                    <th className="tipo-rotulo p-4">E-mail</th>
                    <th className="tipo-rotulo p-4">Telefone</th>
                    <th className="tipo-rotulo p-4">Situação</th>
                    <th className="tipo-rotulo p-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {visiveis.map((u) =>
                    editandoId === u.id ? (
                      <tr key={u.id} className="border-b border-border last:border-0">
                        <td colSpan={5} className="p-4">
                          <form onSubmit={(e) => salvar(e, u)} className="grid gap-5 md:grid-cols-3">
                            <Campos form={edicao} setForm={setEdicao} prefixo={`e-${u.id}`} />
                            <div className="flex gap-3 md:col-span-3">
                              <Button type="submit" variant="secundario" disabled={ocupado === u.id}>
                                {ocupado === u.id ? <Spinner /> : 'Salvar alterações'}
                              </Button>
                              <Button
                                type="button"
                                variant="fantasma"
                                onClick={() => setEditandoId(null)}
                              >
                                Cancelar
                              </Button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    ) : (
                      <tr
                        key={u.id}
                        className="border-b border-border transition-colors duration-[var(--dur-fast)] last:border-0 hover:bg-surface-hover"
                      >
                        <td className="p-4 text-[15px]">
                          {u.nome}
                          {u.id === eu?.id && <span className="ml-2 text-text-tertiary">(você)</span>}
                        </td>
                        {/* Identificadores em mono (manual §2). */}
                        <td className="tipo-dado p-4 text-text-secondary">{u.email}</td>
                        <td className="tipo-dado p-4 text-text-secondary">
                          {formatarTelefone(u.telefone)}
                        </td>
                        <td className="p-4">
                          {u.estado === 'arquivado' ? (
                            <Chip className="opacity-60">arquivado</Chip>
                          ) : u.estado === 'inativo' ? (
                            <Chip>inativo</Chip>
                          ) : u.deve_trocar_senha ? (
                            <Chip className="border-warning/40 text-warning">senha temporária</Chip>
                          ) : (
                            <Chip className="border-success/40 text-success">ativo</Chip>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="fantasma"
                              size="sm"
                              onClick={() => {
                                setEdicao({ nome: u.nome, email: u.email, telefone: u.telefone });
                                setEditandoId(u.id);
                              }}
                            >
                              <Pencil /> Editar
                            </Button>
                            <Button
                              variant="fantasma"
                              size="sm"
                              disabled={ocupado === u.id}
                              onClick={() => void resetarSenha(u)}
                              title="Gera uma senha temporária nova e a envia por e-mail"
                            >
                              <KeyRound /> Resetar senha
                            </Button>
                            {u.id !== eu?.id && (
                              <>
                                {u.estado === 'ativo' && (
                                  <Button
                                    variant="fantasma"
                                    size="sm"
                                    disabled={ocupado === u.id}
                                    onClick={() => void mudarEstado(u, 'desativar')}
                                  >
                                    <UserX /> Desativar
                                  </Button>
                                )}
                                {u.estado !== 'ativo' && (
                                  <Button
                                    variant="fantasma"
                                    size="sm"
                                    disabled={ocupado === u.id}
                                    onClick={() => void mudarEstado(u, 'reativar')}
                                  >
                                    {u.estado === 'arquivado' ? <ArchiveRestore /> : <UserCheck />}
                                    Reativar
                                  </Button>
                                )}
                                {/* Arquivar só aparece para quem já está inativo, e
                                    excluir só para quem já está arquivado. A escada
                                    impede que um clique distraído chegue ao fim. */}
                                {u.estado === 'inativo' && (
                                  <Button
                                    variant="fantasma"
                                    size="sm"
                                    disabled={ocupado === u.id}
                                    onClick={() => void mudarEstado(u, 'arquivar')}
                                  >
                                    <Archive /> Arquivar
                                  </Button>
                                )}
                                {u.estado === 'arquivado' && (
                                  <Button
                                    variant="fantasma"
                                    size="sm"
                                    className="text-danger hover:text-danger"
                                    disabled={ocupado === u.id}
                                    onClick={() => setAExcluir(u)}
                                  >
                                    <Trash2 /> Excluir
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {arquivados > 0 && (
          <div className="border-t border-border px-4 py-3">
            <Button
              variant="fantasma"
              size="sm"
              onClick={() => setMostrarArquivados((v) => !v)}
            >
              {mostrarArquivados
                ? 'Ocultar arquivados'
                : `Mostrar ${arquivados} arquivado${arquivados > 1 ? 's' : ''}`}
            </Button>
          </div>
        )}
      </Card>

      {/* Ação irreversível: o impacto é nomeado, o botão é de perigo, e o foco
          inicial fica no Cancelar (manual §10). */}
      <ConfirmarAcao
        aberto={aExcluir !== null}
        onAbrir={(v) => !v && setAExcluir(null)}
        titulo="Excluir usuário"
        rotuloConfirmar="Excluir usuário"
        ocupado={ocupado === aExcluir?.id}
        onConfirmar={() => aExcluir && void excluir(aExcluir)}
        impacto={
          aExcluir && (
            <>
              <p>
                Isto removerá permanentemente a conta de{' '}
                <span className="text-text">{aExcluir.nome}</span> (
                <span className="tipo-dado">{aExcluir.email}</span>). Ele perderá o acesso
                imediatamente e não há como desfazer.
              </p>
              <p className="mt-3">
                O registro de tudo que ele consultou <strong className="text-text">permanece</strong>{' '}
                no log de auditoria — excluir a conta não apaga o rastro.
              </p>
              <p className="mt-3 text-text-tertiary">
                Se a intenção é apenas cortar o acesso, prefira <em>Desativar</em>: é reversível.
              </p>
            </>
          )
        }
      />
    </div>
  );
}
